import { createSelector, lruMemoize } from 'reselect';

import {
    liveCheckTripAction,
    liveCheckSelectors,
} from 'wanderu-ducks-live-check';
import { tripSelectors } from 'wanderu-ducks-trips';
import { createSelectorFactory } from 'wanderu-ducks-utils';
import { selectCurrency } from 'wanderu-i18n';

import NAMESPACE from './types';
import SLICE_NAMESPACE from '../../types';

function selectData(state) {
    return state[SLICE_NAMESPACE][NAMESPACE];
}

function selectTravelerInfo(state) {
    return selectData(state);
}

function selectTripIdsWithMismatchedCurrencyFactory(tripId) {
    return createSelector(
        selectCurrency,
        (state) => tripSelectors.selectTripSet(state, tripId),
        (selectedCurrency, trips) => {
            return trips.reduce((arr, trip) => {
                if (!Object.prototype.hasOwnProperty.call(trip.pricing[0], selectedCurrency)) {
                    arr.push(trip.tripId);
                }
                return arr;
            }, []);
        },
    );
}

const selectTripIdsWithMismatchedCurrency = createSelectorFactory(
    selectTripIdsWithMismatchedCurrencyFactory,
);

function selectItineraryFactory(tripId) {
    return createSelector(
        (state) => state,
        (state) => tripSelectors.selectLegIds(state, tripId),
        (state, tripIds) => {
            if (!tripId || !tripIds) {
                return [];
            }

            return tripIds.map(
                (id) => tripSelectors.selectTripItinerary(state, id),
            );
        },
    );
}
const selectItinerary = createSelectorFactory(selectItineraryFactory);

const selectPrices = lruMemoize(
    (state, tripIds, seats, wheelchairs, showTotalPrice = false) => {
        const ids = tripIds.filter(Boolean);

        return ids.reduce((idSet, id) => {
            const tripPrices = tripSelectors
                .selectTripPrices(state, id, seats, wheelchairs, showTotalPrice);
            const pricing = tripPrices?.pricing || [];
            return [...idSet, ...pricing];
        }, []);
    },
);

function selectAvailableFares(state, tripId, seats, wheelchairs) {
    return tripSelectors.selectAvailableFares(state, tripId, seats, wheelchairs);
}

const selectAvailableFareNames = createSelector(
    [tripSelectors.selectAvailableFares],
    (fares = {}) => {
        const tripIdsList = Object.keys(fares);
        const tripFareMap = {};

        for (const tripId of tripIdsList) {
            const hasFares = Boolean(fares[tripId].length);
            const notInMap = !tripFareMap[tripId];

            if (hasFares && notInMap) {
                tripFareMap[tripId] = fares[tripId].map(fare => fare.description.title);
            }
        }

        return tripFareMap;
    }
);
function selectLcParams(state, tripId, opts) {
    const {
        seats,
        wheelchairs,
    } = opts || selectTravelerInfo(state);
    const {
        selectedFare,
    } = tripSelectors.selectTripData(state, tripId) || {};

    return {
        seats,
        selectedFare,
        tripId,
        wheelchairs,
    };
}

function selectNativeBookButtonSupport(state, tripIds, opts) {
    const lcsCompleted = selectLiveChecksCompleted(state, tripIds, opts);
    if (!lcsCompleted) {
        return undefined; // need to wait for all LCs to complete before knowing for sure
    }

    return tripIds.length > 0 && tripIds.every((tripId) => {
        const lcParams = selectLcParams(state, tripId, opts);
        return liveCheckSelectors.selectTripNativeBooking(state, lcParams);
    });
}

function selectSingleDeeplinkBookButtonSupport(state, tripIds, opts) {
    const lcsCompleted = selectLiveChecksCompleted(state, tripIds, opts);
    if (!lcsCompleted) {
        return undefined; // need to wait for all LC calls to complete before knowing for sure
    }

    if (tripIds.length === 0) {
        return false;
    }

    /* GBREZ trips include a `roundTripEnabled` field which is a list of deeplink trip ids
     * that constitute a round-trip.  We only want to show a single book button for them
     * it's safe to assume that if one trip has this field then they all have this field.
     * we don't (currently) route GBREZ with other carriers */

    const lcParams = selectLcParams(state, tripIds[0], opts);
    let { roundTripBundle = [] } = liveCheckSelectors.selectTripCheckoutData(
        state,
        lcParams,
    ) || {};

    return tripIds.every((tripId) => roundTripBundle.includes(tripId));
}

const selectSingleBookButtonSupport = createSelector(
    selectNativeBookButtonSupport,
    selectSingleDeeplinkBookButtonSupport,
    (native, singleDeepLink) => (native === undefined || singleDeepLink === undefined)
        ? undefined
        : !!native || !!singleDeepLink,
);

function selectCheckoutData(state, tripIds = [], opts = {}) {
    if (!tripIds.length) {
        return undefined;
    }

    return tripIds.filter(Boolean)
        .reduce((allIds, tripId) => [
            ...allIds,
            ...tripSelectors.selectLegIds(state, tripId),
        ], [])
        .reduce((checkoutData, tripId) => {
            const lcParams = selectLcParams(state, tripId, opts);
            return {
                ...checkoutData,
                [tripId]: liveCheckSelectors.selectTripCheckoutData(state, lcParams),
            };
        }, {});
}

function selectCarrierDataHelper(state, tripIds) {
    if (!tripIds || !tripIds.length) {
        return undefined;
    }

    const ids = tripIds.filter(Boolean).reduce((arr, tripId) => ([
        ...arr,
        ...tripSelectors.selectLegIds(state, tripId),
    ]), []);

    return ids.reduce((arr, id) => [
        ...arr,
        tripSelectors.selectTripCarrierData(state, id),
    ], []);
}

function selectCarrierDataFactory(tripIds) {
    return createSelector(
        (state) => selectCarrierDataHelper(state, tripIds),
        carriers => carriers,
    );
}
const selectCarrierData = createSelectorFactory(selectCarrierDataFactory);

function selectServiceAttributionFactory(tripIds) {
    return createSelector(
        (state) => selectCarrierDataHelper(state, tripIds),
        (carriers) => {
            if (!carriers || !carriers.length) {
                return undefined;
            }

            const services = carriers.reduce((serviceNameList, carrier) => {
                if (carrier.supplementalInfo && carrier.supplementalInfo.serviceAttribution) {
                    serviceNameList.push(carrier.supplementalInfo.serviceAttribution.name);
                }
                return serviceNameList;
            }, []);

            return services[0] || '';
        },
    );
}
const selectServiceAttribution = createSelectorFactory(selectServiceAttributionFactory);

function selectTripPrice(state, tripId, seats, wheelchairs, showTotalPrice = false) {
    /* the wanderu-ducks-live-check selector returns pricing as an array
     * as a convenience for search results which display routed trips,
     * but we only ever select non-routed trips here */
    const { fees, pricing } = tripSelectors.selectTripPrices(
        state,
        tripId,
        seats,
        wheelchairs,
        showTotalPrice
    ) || [];
    return { fees, pricing };
}

function selectLiveChecksInProgress(state, tripIds, opts) {
    return tripIds.some((tripId) => {
        const lcParams = selectLcParams(state, tripId, opts);
        return liveCheckTripAction.selectWorking(state, lcParams);
    });
}

function selectLiveChecksCompleted(state, tripIds, opts) {
    return tripIds.every((tripId) => {
        const lcParams = selectLcParams(state, tripId, opts);
        const hasErrored = !!liveCheckTripAction.selectError(state, lcParams);
        const hasSucceeded = liveCheckTripAction.selectSuccess(state, lcParams);
        return hasErrored || hasSucceeded;
    });
}

function selectLiveChecksSuccess(state, tripIds, opts) {
    return tripIds.every((tripId) => {
        const lcParams = selectLcParams(state, tripId, opts);
        return liveCheckTripAction.selectSuccess(state, lcParams);
    });
}

function selectLiveCheckEarliestExpiresAt(state, tripIds, opts) {
    return tripIds.reduce((earliestExpiresAt, tripId) => {
        const lcParams = selectLcParams(state, tripId, opts);
        const expiresAt = liveCheckTripAction.selectExpiresAt(state, lcParams) || 0;

        if (typeof earliestExpiresAt === 'undefined' || expiresAt < earliestExpiresAt) {
            return expiresAt;
        }

        return earliestExpiresAt;
    }, undefined);
}

function selectLiveChecksHaveExpired(state, tripIds, opts) {
    return tripIds.some((tripId) => {
        const lcParams = selectLcParams(state, tripId, opts);
        return liveCheckTripAction.selectExpired(state, lcParams);
    });
}

function selectLiveCheckSummaryStatus(state, tripIds, opts) {
    const lcParamsArr = tripIds.map((tripId) => selectLcParams(state, tripId, opts));
    const originalPricing = tripIds.reduce((pricingMap, tripId) => {
        const trip = tripSelectors.selectTripData(state, tripId) || {};
        return {
            ...pricingMap,
            [tripId]: trip.pricing,
        };
    }, {});

    return liveCheckSelectors.selectLiveCheckSummaryStatus(state, lcParamsArr, originalPricing);
}

function selectLiveCheckData(state, tripIds, opts) {
    const lcParamsArr = tripIds.map((tripId) => selectLcParams(state, tripId, opts));
    const originalPricing = tripIds.reduce((pricingMap, tripId) => {
        const trip = tripSelectors.selectTripData(state, tripId) || {};
        return {
            ...pricingMap,
            [tripId]: trip.pricing,
        };
    }, {});

    return liveCheckSelectors.selectLiveCheckData(state, lcParamsArr, originalPricing);
}

function selectSeatInformation(state, tripIds) {
    const tripList = tripIds.reduce((arr, tripId) => {
        const trip = tripSelectors.selectTrip(state, tripId);
        const carrier = tripSelectors.selectTripCarrierData(state, tripId);
        const isAdaCompliant = !!(trip.accessibility
            && trip.accessibility.maxWheelchairSeats);

        arr.push({
            carrierName: carrier.name,
            isAdaCompliant,
            maxSeats: carrier.maxSeats,
            maxWheelchairs: isAdaCompliant
                ? trip.accessibility.maxWheelchairSeats
                : 0,
            tripId: trip.tripId,
        });

        return arr;
    }, []);

    const maxSeats = Math.min(...tripList.map((trip) => trip.maxSeats));
    const maxWheelchairs = Math.min(...tripList.map((trip) => trip.maxWheelchairs));

    const nonAdaCarriers = tripList.reduce((arr, trip) => {
        if (!trip.isAdaCompliant) {
            arr.add(trip.carrierName);
        }
        return arr;
    }, new Set());

    return {
        maxSeats,
        maxWheelchairs,
        nonAdaCarriers: [...nonAdaCarriers],
    };
}

export {
    selectAvailableFareNames,
    selectAvailableFares,
    selectCarrierData,
    selectCheckoutData,
    selectItinerary,
    selectLiveCheckData,
    selectLiveCheckEarliestExpiresAt,
    selectLiveCheckSummaryStatus,
    selectLiveChecksCompleted,
    selectLiveChecksHaveExpired,
    selectLiveChecksInProgress,
    selectLiveChecksSuccess,
    selectPrices,
    selectSeatInformation,
    selectServiceAttribution,
    selectSingleBookButtonSupport,
    selectSingleDeeplinkBookButtonSupport,
    selectTravelerInfo,
    selectTripIdsWithMismatchedCurrency,
    selectTripPrice,
};
